import React, { useState, useMemo } from 'react'
import { Stack, Box, Heading, Text, SearchableSelect, InputGroup, Input, SimpleTable, Checkbox, Badge } from '@/components/lib'
import { FaSearch } from 'react-icons/fa'
import { TRIAL_PERMISSIONS, GROUP_PERMISSIONS, SITE_PERMISSIONS } from '@/utils/utils'

export default function PermissionChooser ({ trial, groups, sites, permissions, onScopedChange }) {
  const [newPermissionScope, setNewPermissionScope] = useState(trial._id)
  const [filterPermissions, setFilterPermissions] = useState('')

  const availablePermissionsForScope = useMemo(() => {
    if (newPermissionScope === trial._id) return TRIAL_PERMISSIONS
    if (groups.some(g => g._id === newPermissionScope)) return GROUP_PERMISSIONS
    if (sites.some(s => s._id === newPermissionScope)) return SITE_PERMISSIONS
    return []
  }, [newPermissionScope, groups, sites])

  const togglePermission = (permission) => {
    const newPermissions = Object.assign([], permissions[newPermissionScope] || [])
    const index = newPermissions.indexOf(permission)
    if (index === -1) {
      newPermissions.push(permission)
    } else {
      newPermissions.splice(index, 1)
    }
    onScopedChange(newPermissionScope, newPermissions)
  }

  const filteredPermissions = filterPermissions.length > 0
    ? availablePermissionsForScope.filter(p => {
      return p.key.toLowerCase().includes(filterPermissions.toLowerCase()) || p.description.toLowerCase().includes(filterPermissions.toLowerCase())
    })
    : availablePermissionsForScope

  return (
    <Stack gap={5}>
      <Box>
        <Heading size='sm'>Permission assignments</Heading>
        <Text>Assign permissions for the whole trial, or for a specific group or site.</Text>
      </Box>
      <SearchableSelect
        value={newPermissionScope}
        onChange={s => {
          setNewPermissionScope(s)
        }}
        options={(() => {
          const options = []
          if (trial._id) options.push({ value: trial._id, label: `Trial-level: ${trial.name} (${permissions[trial._id]?.length || 0} permissions)` })
          if (groups.length > 0) options.push(...groups.map(g => ({ value: g._id, label: `Group-level: ${g.name} (${permissions[g._id]?.length || 0} permissions)` })))
          if (sites.length > 0) options.push(...sites.map(s => ({ value: s._id, label: `Site-level: ${s.name} (${permissions[s._id]?.length || 0} permissions)` })))
          return options
        })()}
      />

      <Box>
        <InputGroup startElement={<FaSearch />}>
          <Input size='sm' variant='flushed' placeholder='Filter permissions...' value={filterPermissions} onChange={e => setFilterPermissions(e.target.value)} />
        </InputGroup>

        <SimpleTable
          size='sm'
          headers={['', 'Name', 'Description']}
          rows={filteredPermissions.map(p => [
            <Checkbox key={p.key} checked={permissions[newPermissionScope]?.includes(p.key)} onChange={() => togglePermission(p.key)} />,
            <Badge key={p.key} size='sm' maxW='150px' overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>{p.key}</Badge>,
            <Text key={p.key}>{p.description}</Text>
          ])}
        />
      </Box>
    </Stack>
  )
}
